import { type AxiosError, isAxiosError } from 'axios';
import { useCallback } from 'react';
import { type ErrorOption, useNotifyTranslatedNetworkError } from './useNotifyTranslatedNetworkError';

const UNKNOWN_ERROR_TRANSLATION_KEY = 'errors.unknown';

export type HandleNetworkErrorOptions<T> = {
  custom?: ErrorOption<AxiosError<T> | Error>;
} & Partial<Record<AxiosErrorStatus, ErrorOption<AxiosError<T>>>>;

type AxiosErrorStatus = 'connection' | number;

type HandleNetworkErrorParameters<T> = {
  error: AxiosError<T> | Error;
  options?: HandleNetworkErrorOptions<T>;
};

const defaultErrorHandlers: Record<number, ErrorOption<AxiosError>> = {
  400: UNKNOWN_ERROR_TRANSLATION_KEY,
  401: false,
  403: 'errors.forbidden',
  404: 'errors.notFound',
  422: UNKNOWN_ERROR_TRANSLATION_KEY,
};

export const useHandleNetworkError = () => {
  const notifyTranslatedNetworkError = useNotifyTranslatedNetworkError();

  return useCallback(
    <T>({ error, options }: HandleNetworkErrorParameters<T>) => {
      if (isAxiosError(error)) {
        if (!error.response) {
          notifyTranslatedNetworkError({
            defaultTranslationKey: UNKNOWN_ERROR_TRANSLATION_KEY,
            error,
            errorOption: options?.connection,
          });
          return;
        }

        const { status } = error.response;
        const errorOption = options?.[status] ?? defaultErrorHandlers[status];

        if (errorOption !== undefined) {
          notifyTranslatedNetworkError({ defaultTranslationKey: UNKNOWN_ERROR_TRANSLATION_KEY, error, errorOption });
          return;
        }
      }

      notifyTranslatedNetworkError({
        defaultTranslationKey: UNKNOWN_ERROR_TRANSLATION_KEY,
        error,
        errorOption: options?.custom,
      });
    },
    [notifyTranslatedNetworkError],
  );
};
